/**
 * Google Picker — the user-facing file chooser for Drive import.
 *
 * Why the Picker: with the narrow `drive.file` scope, Memoryflix can only
 * touch files the user explicitly chooses here. It never sees a listing of
 * their Drive. Google's script is loaded on demand, only when the user opens
 * the Picker, so nothing from Google runs on any other page.
 *
 * Docs: https://developers.google.com/workspace/drive/picker/guides/overview
 */

export interface PickedDoc {
  id: string;
  name?: string;
  mimeType?: string;
  resourceKey?: string;
}

// Minimal typings for the parts of the Picker API used here.
interface PickerView {
  setMimeTypes(types: string): PickerView;
  setIncludeFolders(on: boolean): PickerView;
  setSelectFolderEnabled(on: boolean): PickerView;
  setEnableDrives(on: boolean): PickerView;
  setParent(id: string): PickerView;
  setFileIds(ids: string): PickerView;
}
interface PickerBuilder {
  addView(view: PickerView): PickerBuilder;
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(id: string): PickerBuilder;
  setCallback(cb: (data: PickerResponse) => void): PickerBuilder;
  enableFeature(feature: string): PickerBuilder;
  setMaxItems(n: number): PickerBuilder;
  setTitle(title: string): PickerBuilder;
  build(): { setVisible(v: boolean): void; dispose(): void };
}
interface PickerResponse {
  action: string;
  docs?: Array<{ id: string; name?: string; mimeType?: string; resourceKey?: string }>;
}
interface PickerNamespace {
  DocsView: new (viewId?: string) => PickerView;
  PickerBuilder: new () => PickerBuilder;
  ViewId: { DOCS: string };
  Feature: { MULTISELECT_ENABLED: string; SUPPORT_DRIVES: string };
  Action: { PICKED: string; CANCEL: string };
}
declare global {
  interface Window {
    gapi?: { load(name: string, cb: { callback: () => void; onerror: () => void }): void };
    google?: { picker?: PickerNamespace };
  }
}

/** Formats Memoryflix can import; everything else is hidden in the Picker. */
const MEDIA_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/avif',
  'image/tiff',
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/webm',
  'video/x-matroska',
  'video/x-msvideo',
  'video/3gpp',
].join(',');

let loading: Promise<PickerNamespace> | null = null;

function loadPickerApi(): Promise<PickerNamespace> {
  if (window.google?.picker) return Promise.resolve(window.google.picker);
  if (loading) return loading;
  loading = new Promise<PickerNamespace>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.onerror = () => reject(new Error('Couldn’t load Google Drive. Check your connection and try again.'));
    script.onload = () => {
      window.gapi!.load('picker', {
        callback: () => resolve(window.google!.picker!),
        onerror: () => reject(new Error('Couldn’t load the Google Drive picker.')),
      });
    };
    document.head.appendChild(script);
  }).catch((err) => {
    loading = null; // allow a later retry
    throw err;
  });
  return loading;
}

export type PickerTarget =
  | { kind: 'browse' }
  | { kind: 'file'; id: string }
  | { kind: 'folder'; id: string };

/**
 * Open the Picker and resolve with the chosen files (empty if cancelled).
 * Folders are navigable but not selectable: the user picks the photos and
 * videos inside, so access is granted file by file.
 */
export async function openDrivePicker(opts: {
  accessToken: string;
  apiKey: string;
  appId: string;
  maxItems: number;
  target: PickerTarget;
}): Promise<PickedDoc[]> {
  const picker = await loadPickerApi();

  const views: PickerView[] = [];
  if (opts.target.kind === 'file') {
    // A pasted file link: show just that file so the user can grant it.
    views.push(new picker.DocsView(picker.ViewId.DOCS).setFileIds(opts.target.id));
  } else {
    const myDrive = new picker.DocsView(picker.ViewId.DOCS)
      .setMimeTypes(MEDIA_MIME_TYPES)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false);
    if (opts.target.kind === 'folder') myDrive.setParent(opts.target.id);
    views.push(myDrive);
    if (opts.target.kind === 'browse') {
      views.push(
        new picker.DocsView(picker.ViewId.DOCS)
          .setMimeTypes(MEDIA_MIME_TYPES)
          .setIncludeFolders(true)
          .setSelectFolderEnabled(false)
          .setEnableDrives(true),
      );
    }
  }

  return new Promise<PickedDoc[]>((resolve) => {
    let builder = new picker.PickerBuilder()
      .setOAuthToken(opts.accessToken)
      .setDeveloperKey(opts.apiKey)
      .setAppId(opts.appId)
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .enableFeature(picker.Feature.SUPPORT_DRIVES)
      .setMaxItems(opts.maxItems)
      .setTitle('Choose photos and videos to import');
    for (const v of views) builder = builder.addView(v);
    const instance = builder
      .setCallback((data) => {
        if (data.action === picker.Action.PICKED) {
          instance.dispose();
          resolve(
            (data.docs ?? []).map((d) => ({
              id: d.id,
              name: d.name,
              mimeType: d.mimeType,
              resourceKey: d.resourceKey,
            })),
          );
        } else if (data.action === picker.Action.CANCEL) {
          instance.dispose();
          resolve([]);
        }
      })
      .build();
    instance.setVisible(true);
  });
}
